import { useScrollReveal, revealClass } from '../../../hooks/useScrollReveal';

const affiliations = [
  {
    id: 1,
    role: 'Senior Curator',
    organisation: 'Nigeria-Brazil Public History Project',
    description: 'Leading curatorial research and programming for this landmark bilateral initiative exploring the deep historical, cultural, and human connections between Nigeria and Brazil, tracing the transatlantic journeys of the Yoruba diaspora and their enduring influence on Brazilian society, religion, and art.',
    logoUrl: 'https://storage.readdy-site.link/project_files/4630a528-7d86-42ab-a7d2-89b69416f273/36efea0d-98eb-4dd2-9e67-3eec462a426c_NBPHP_Logo.png?v=78da4ef9106922c88f5a5e211af792be',
    logoBg: '#ffffff',
    region: 'Lagos – Brazil',
    pending: false,
  },
  {
    id: 2,
    role: 'Associate Fellow',
    organisation: 'Royal Historical Society',
    description: 'Recognised as an Associate Fellow of the Royal Historical Society, one of the United Kingdom\'s most prestigious learned societies dedicated to the promotion and support of historical scholarship, research, and publication at the highest international level.',
    logoUrl: 'https://storage.readdy-site.link/project_files/4630a528-7d86-42ab-a7d2-89b69416f273/f52aa4fb-f195-4f2e-91bb-40360bc3ad7b_RHS_UK_Logo.jpg?v=8c3d790603e09f617d1c01e5fb0f087d',
    logoBg: '#ffffff',
    region: 'United Kingdom',
    pending: false,
  },
  {
    id: 3,
    role: 'Member',
    organisation: 'International Council on Archives (ICA)',
    description: 'Active member of the International Council on Archives, the global professional body representing archives and archivists worldwide, committed to advancing archival standards, access, and the preservation of the world\'s documentary heritage.',
    logoUrl: 'https://storage.readdy-site.link/project_files/4630a528-7d86-42ab-a7d2-89b69416f273/7d1493ff-7405-4971-a132-b364712bcd10_ICA_Logo.jpg?v=da6bdf2f6b60b7e5684223303d8b2d4b',
    logoBg: '#ffffff',
    region: 'Paris, France',
    pending: false,
  },
  {
    id: 4,
    role: 'Executive Committee Member',
    organisation: 'ICA Section on Archives of Literature and Art (SLA)',
    description: 'Elected to the Executive Committee of the International Council on Archives\' Section on Archives of Literature and Art for the 2025-2029 term, contributing to global policy, standards, and advocacy for the preservation of literary and artistic archives worldwide.',
    logoUrl: 'https://storage.readdy-site.link/project_files/4630a528-7d86-42ab-a7d2-89b69416f273/7d1493ff-7405-4971-a132-b364712bcd10_ICA_Logo.jpg?v=da6bdf2f6b60b7e5684223303d8b2d4b',
    logoBg: '#ffffff',
    region: 'Paris, France',
    pending: false,
  },
  {
    id: 5,
    role: 'RSA Fellow',
    organisation: 'Royal Society of Arts (RSA)',
    description: 'Fellow of the Royal Society of Arts (RSA), a globally respected institution committed to social progress, innovation, and positive societal impact. The fellowship recognizes contributions to public history, archival preservation, cultural storytelling, and ongoing efforts to safeguard and promote African heritage for present and future generations.',
    logoUrl: 'https://storage.readdy-site.link/project_files/4630a528-7d86-42ab-a7d2-89b69416f273/a176aa85-ed17-4a0b-8953-8f4b14f12b75_RSA_Logo.png?v=3c3a1f7c5225c6fed54877550b6500a6',
    logoBg: '#ffffff',
    region: 'United Kingdom',
    pending: false,
  },
  {
    id: 6,
    role: 'Life Member',
    organisation: 'Lagos Studies Association (LSA)',
    description: 'Life Member of the Lagos Studies Association (LSA), an international interdisciplinary organization dedicated to advancing scholarship, policy, culture, and public engagement concerning Lagos, Nigeria, and Africa. This affiliation reflects a sustained commitment to the preservation of African history, the promotion of cultural heritage, and the advancement of meaningful dialogue between academic scholarship and public history.',
    logoUrl: 'https://storage.readdy-site.link/project_files/4630a528-7d86-42ab-a7d2-89b69416f273/937c201a-53a1-4008-89ab-984be69afe5a_LSA-Logo.jpeg?v=4fd12510984dbfd031f3ce7f945db6bf',
    logoBg: '#ffffff',
    region: 'Lagos, Nigeria',
    pending: false,
  },
];

function OrgLogo({ aff }: { aff: typeof affiliations[0] }) {
  return (
    <div
      className="w-24 h-24 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden p-1"
      style={{ backgroundColor: aff.logoBg, border: '1px solid rgba(255,255,255,0.15)' }}
    >
      <img
        src={aff.logoUrl}
        alt={aff.organisation}
        className="w-full h-full object-contain"
        onError={(e) => {
          const target = e.currentTarget as HTMLImageElement;
          target.style.display = 'none';
          const parent = target.parentElement;
          if (parent) {
            parent.innerHTML = `<span style="color:#b89a6a;font-weight:700;font-size:13px;font-family:serif;letter-spacing:0.05em">${aff.organisation.slice(0, 2).toUpperCase()}</span>`;
          }
        }}
      />
    </div>
  );
}

export default function AffiliationsSection() {
  const { ref: headingRef, isVisible: headingVisible } = useScrollReveal<HTMLDivElement>();
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.05 });

  return (
    <section id="affiliations" className="bg-[#1a1a18] py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div ref={headingRef} className={`mb-16 ${revealClass(headingVisible)}`}>
          <p className="text-xs uppercase tracking-widest text-[#b89a6a] mb-4">GLOBAL ENGAGEMENT</p>
          <h2 className="font-serif text-4xl lg:text-5xl font-bold text-white mb-6">
            International Affiliations
          </h2>
          <p className="font-sans text-base text-gray-400 max-w-2xl">
            Active membership and leadership roles in international bodies advancing archival science, cultural heritage, and public history across continents.
          </p>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {affiliations.map((item, index) => (
            <div
              key={item.id}
              className={`border rounded-lg p-8 transition-colors duration-300 flex flex-col gap-6 ${
                item.pending
                  ? 'border-[#b89a6a]/40 bg-[#b89a6a]/5'
                  : 'border-white/10 hover:border-[#b89a6a]/50'
              } ${revealClass(gridVisible, Math.min(index * 100, 400))}`}
            >
              <div className="flex items-start justify-between gap-4">
                <OrgLogo aff={item} />
                <div className="flex flex-col items-end gap-1">
                  <span className="text-xs uppercase tracking-widest text-gray-500 text-right whitespace-nowrap">
                    {item.region}
                  </span>
                  {item.pending && (
                    <span className="text-xs bg-[#b89a6a]/20 text-[#b89a6a] px-2 py-0.5 rounded-full whitespace-nowrap">
                      Pending Confirmation
                    </span>
                  )}
                </div>
              </div>

              <div>
                <p className="text-xs uppercase tracking-widest text-[#b89a6a] font-semibold mb-2">
                  {item.role}
                </p>
                <h3 className="font-serif text-lg font-semibold text-white leading-snug">
                  {item.organisation}
                </h3>
              </div>

              <p className="font-sans text-sm text-gray-400 leading-relaxed flex-1">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
